/**
 * WeeklyRollupsTab — the weekly-rollup funnel for one PCO, grouped by
 * submission (PCO → submission → weekly rollups).
 *
 * ONE "Packages" card (2026-08-05) holding every submission as a flat stack,
 * narrowed by count pills — Open · Sent · Approved, the package's real
 * lifecycle (submission status 'closed' renders as "Sent": by close time every
 * week has gone to the GC). Those three stages used to be three fold-out
 * cards; the counts are what a PM reads at a glance and they were buried in
 * the group headers. Each row: submission number · date range · total ·
 * MOD/FWA, expandable to its member weekly rollups. A "Start Next" control
 * opens the StartNextSubmissionDialog, which opens the next package as the new
 * collecting home and closes nothing. Packages close the other way round:
 * Approve & Send offers it on any open submission's last unsent week.
 *
 * BUCKETING IS BY WORK, NOT ONLY BY STATUS (2026-08-19). A package holding any
 * `in_progress` week renders under Open whatever its own status says — see
 * `lib/packageBuckets.ts` for why. Presentation only: the row's actions still
 * come from the package's real status, and resending the reopened week drops
 * it back into its own pile with nothing written. The per-PACKAGE fold follows
 * the same logic — SubmissionSection opens Open and Sent and leaves Approved
 * folded (where the client rulings about what greets you live), plus any
 * package carrying a reopened week, which is now the work surface.
 *
 * Inside a submission, in_progress weeks render the featured Approve & Send
 * card; closed weeks render the compact row with Reopen. The default
 * submission's send card also offers the "close & start next" decision inline,
 * at the moment it naturally happens: when the PM sends the week.
 *
 * APPROVAL IS NOT HERE (client ruling 2026-07-29). There is one approval
 * concept at the T&M level and the submission is the unit — "Mark Approved" on
 * the package (SubmissionSection) cascades to its sent weeks server-side. The
 * week's status chip still shows Pending → Approved; it just isn't the place
 * you set it. Hoffman's approval lives outside our system either way, so the
 * flip remains a manual human-in-the-loop relabel that doesn't touch totals.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, CalendarRange } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useRollupDetail } from '../lib/useRollups';
import { useSettingsProject } from '../lib/useSettings';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import type { UseApiQueryResult } from '../lib/useApiQuery';
import {
  formatDateMd,
  formatMoneyCents,
  formatSubmissionLabel,
  formatSubmissionShort,
  formatWeekdayMonthDay,
  formatWeekRange,
  gcName,
} from '../lib/format';
import { Dropdown, EmptyState, Skeleton, SkeletonRows, StaleDataBanner, StatusPill } from './design';
import { rollupPillKind, rollupStatusLabel, sentOnLabel } from '../lib/rollupStatus';
import { hasReopenedWeek, packageBucketFor } from '../lib/packageBuckets';
import { REVISION_BANNER_LABEL } from '../lib/revisionBanner';
import { EditImportedTotalModal } from './EditImportedTotalModal';
import { rollupActionErrorMessage } from '../lib/rollupErrors';
import { RevisionBannerChrome } from './RevisionBannerChrome';
import { RevisionDetailsTip } from './RevisionDetailsTip';
import { ConfirmModal } from './ConfirmModal';
import { SendComposerModal, type SendComposerPayload } from './SendComposerModal';
import { HelpTip } from './HelpTip';
import { SubmissionSection } from './SubmissionSection';
import { StartNextSubmissionDialog } from './StartNextSubmissionDialog';
import { submissionErrorMessage, submissionErrorMessageForCode } from '../lib/useSubmissions';
import { useToast } from './ToastProvider';
import type {
  PcoSubmissionsResponse,
  RollupDetail,
  RollupListItem,
  RollupRevisionContext,
  RollupStatus,
  SubmissionSummary,
} from '@coffman-tm/shared';

interface Props {
  pcoId: string;
  /** Owned by PcoDetailPage so the tab-bar submission strip reads the same
   *  list — one fetch, one refetch, no drift between the two surfaces. */
  subs: UseApiQueryResult<PcoSubmissionsResponse>;
}

/** Which lifecycle stage the packages list is filtered to. */
type PackageFilter = 'open' | 'closed' | 'approved';

export function WeeklyRollupsTab({ pcoId, subs }: Props) {
  const [startNewOpen, setStartNewOpen] = useState(false);
  // Open by default — landing on the work surface is what the old expanded
  // Open group did, and an "All" default buried it under the history.
  const [packageFilter, setPackageFilter] = useState<PackageFilter>('open');
  // The week just sent from this tab. After the refetch swaps its card for the
  // flat sent row, that row flashes and scrolls into view — the landing for
  // "where did my week go?" (client, 2026-08-02). Cleared after the animation
  // so later refetches don't re-flash.
  const [flashRollupId, setFlashRollupId] = useState<string | null>(null);
  useEffect(() => {
    if (!flashRollupId) return;
    const t = setTimeout(() => setFlashRollupId(null), 2600);
    return () => clearTimeout(t);
  }, [flashRollupId]);

  // All rollup data on this tab comes from the submissions response;
  // per-week detail rows fetch (and refetch) their own detail.
  const refetchAll = async () => {
    await subs.refetch();
  };

  const submissions = subs.data?.submissions ?? [];
  // Default open first, then remaining open, then closed/approved by number desc.
  const ordered = [...submissions].sort((a, b) => {
    const rank = (s: typeof a) =>
      s.is_default_target && s.status === 'open' ? 0 : s.status === 'open' ? 1 : 2;
    return (
      rank(a) - rank(b) ||
      b.submission_number.localeCompare(a.submission_number, undefined, { numeric: true })
    );
  });

  // Response-level parent number — zero-rollup submissions (fresh
  // close-and-next / open-alongside) still label correctly.
  const pcoParentNumber = subs.data?.pco_number ?? '';

  // A package with a reopened week is Open work, not history — packageBucketFor
  // relocates it out of Sent/Approved so the tab's default view holds
  // everything that still needs doing. The pills count the same buckets they
  // render, so they can't disagree with the list.
  const openSubs = ordered.filter((s) => packageBucketFor(s) === 'open');
  const sentSubs = ordered.filter((s) => packageBucketFor(s) === 'closed');
  const approvedSubs = ordered.filter((s) => packageBucketFor(s) === 'approved');

  // The three lifecycle stages used to be three fold-out cards. They are one
  // card with count pills now: the counts are the thing a PM reads at a
  // glance, and they were only visible on a group's own header before. The
  // per-PACKAGE fold is otherwise untouched — SubmissionSection still opens
  // Open and Sent and leaves Approved folded, which is where the 2026-08-01 /
  // 2026-08-02 rulings about what greets you actually live.
  const shown =
    packageFilter === 'open' ? openSubs : packageFilter === 'closed' ? sentSubs : approvedSubs;

  // Client-side next number for "New submission (004.12)" offers — MAX(numeric)+1
  // over every submission on the PCO, mirroring the server's suggestion.
  const nextNumberAcrossPco = String(
    submissions.reduce(
      (m, s) => Math.max(m, Number.parseInt(s.submission_number, 10) || 0),
      0,
    ) + 1,
  );

  const renderSubmission = (s: SubmissionSummary) => (
    <SubmissionSection
      key={s.id}
      pcoParentNumber={pcoParentNumber}
      submission={s}
      defaultExpanded={
        // Sent expands too (client, 2026-08-01): it is at most a package or
        // two awaiting approval, and folding those cost a second click for no
        // saved space. Approved — routinely a dozen — stays folded, UNLESS it
        // holds a reopened week, in which case Open is where it now renders
        // and the week inside it is the reason the PM is here.
        s.status === 'open' || s.status === 'closed' || hasReopenedWeek(s)
      }
      onChanged={refetchAll}
    >
      {s.rollups
        .slice()
        .sort((a, b) => a.week_ending_date.localeCompare(b.week_ending_date))
        .map((r) =>
          r.status === 'in_progress' ? (
            <ActiveRollupRow
              key={r.id}
              pcoId={pcoId}
              rollupId={r.id}
              weekEndingDate={r.week_ending_date}
              // in_progress + stale = REOPENED FOR REVISION. A late PDF
              // auto-reopened this week (server, 2026-07-29); its money has
              // left Submitted-to-date until it is resent, so the row has to
              // say so or the dip looks like a bug.
              reopenedForRevision={r.stale_after_submission === true}
              // What actually landed, when the week was last sent, and what it
              // cost then. Server-side; present only in the reopened state, so
              // its absence is the fallback signal, not an error.
              revisionContext={r.revision_context}
              // Approve & Send is how ANY package ends now, not just the
              // collecting one — a package pushed aside by Start Next still
              // has weeks to send, and sending its last one is the moment it
              // should close.
              offerClose={s.status === 'open'}
              // Other unsent weeks in this package (this one is about to be
              // sent). Nonzero means this is NOT the last week, so there is
              // nothing to offer closing yet.
              carryCount={
                s.rollups.filter((x) => x.status === 'in_progress' && x.id !== r.id).length
              }
              pcoParentNumber={pcoParentNumber}
              moveTargets={submissions.filter((o) => o.status === 'open' && o.id !== s.id)}
              nextNumber={nextNumberAcrossPco}
              onChanged={refetchAll}
              onSent={setFlashRollupId}
            />
          ) : (
            <ClosedRollupRow
              key={r.id}
              pcoId={pcoId}
              rollup={r}
              flash={r.id === flashRollupId}
              onChanged={refetchAll}
            />
          ),
        )}
    </SubmissionSection>
  );

  return (
    <div className="section section--card">
      {/* Packages card head: what this card holds, how to narrow it, and the
          one action that creates another. Start Next is always available, not
          gated on an open default — a PCO whose last package closed on send
          has nothing collecting, which is exactly when the next one needs
          starting, and the old gate hid the button then. It keeps its real
          name: it opens the next submission PACKAGE, which is not the same act
          as starting a week, and packages are what this list is made of. */}
      <div className="section-head" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
        <div className="section-head-left" style={{ flexWrap: 'wrap' }}>
          {/* No "Packages" title (Brandon 2026-08-05) — the pills say what
              this card holds; the word said it twice. */}
          <div
            role="group"
            aria-label="Filter packages by status"
            style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}
          >
            <PackagePill
              label="Open"
              count={openSubs.length}
              active={packageFilter === 'open'}
              onClick={() => setPackageFilter('open')}
            />
            <PackagePill
              label="Sent"
              count={sentSubs.length}
              active={packageFilter === 'closed'}
              onClick={() => setPackageFilter('closed')}
            />
            <PackagePill
              label="Approved"
              count={approvedSubs.length}
              active={packageFilter === 'approved'}
              onClick={() => setPackageFilter('approved')}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <HelpTip title="What Start Next does" learnMore="weekly-rollups">
            Opens this PCO&apos;s next submission as the new collecting home — new work lands
            there from now on. It closes nothing: the package that was collecting stays open
            and finishes through Approve &amp; Send on its last unsent week.
          </HelpTip>
          {/* No native title here — the HelpTip above is the single source of
              truth for this button's explanation, so the two can't drift. */}
          <button type="button" className="btn btn-green" onClick={() => setStartNewOpen(true)}>
            Start Next
          </button>
        </div>
      </div>

      {/* Two distinct failures, two distinct messages. Nothing loaded yet is a
          hard failure. A failure *with* rows already on screen used to render
          nothing at all — the list just quietly stopped being current. */}
      {subs.error && !subs.data && (
        <p className="muted" style={{ fontSize: 13 }}>
          Couldn&apos;t load submissions. {subs.error.message}{' '}
          <button type="button" className="btn-link" onClick={() => void subs.refetch()}>
            Retry
          </button>
        </p>
      )}
      {subs.data && (
        <StaleDataBanner
          key={subs.error?.message}
          error={subs.error}
          onRetry={() => void subs.refetch()}
          message="Couldn't refresh submissions — showing last loaded data."
        />
      )}

      {/* Nothing rendered here at all before this — a blank gap while the
          submissions fetch was in flight. Same row-shaped skeleton as the
          inbox master list; three bars reads as "a short stack is coming"
          without overcommitting to how many submissions actually exist. */}
      {subs.isLoading && <SkeletonRows rows={3} />}

      {!subs.isLoading && ordered.length === 0 && (
        <EmptyState
          flush
          icon={CalendarRange}
          text="No submissions yet. The next foreman PDF opens submission 1."
        />
      )}

      {/* Workflow groups — Open → Sent → Approved, the package's real
          lifecycle, except that anything holding a reopened week joins Open
          (packageBuckets.ts). Open renders expanded (it's the work surface);
          the history groups start folded. Empty Sent/Approved groups hide;
          Open always shows so "nothing is open right now" is visible state,
          not an absence. */}
      {ordered.length > 0 && (
        <>
          {/* "Nothing is open right now" is visible state, not an absence —
              the old Open group always rendered for exactly this reason, and
              the filtered list has to keep saying it. */}
          {shown.length === 0 && packageFilter === 'open' && (
            <p className="muted" style={{ fontSize: 13, margin: '0 0 12px' }}>
              No open submission. Use &ldquo;Start Next&rdquo; above, or the next foreman
              PDF opens one automatically.
            </p>
          )}
          {shown.length === 0 && packageFilter !== 'open' && (
            <p className="muted" style={{ fontSize: 13, margin: '0 0 12px' }}>
              No packages in this state yet.
            </p>
          )}
          {shown.map(renderSubmission)}
        </>
      )}

      {startNewOpen && (
        <StartNextSubmissionDialog
          pcoId={pcoId}
          pcoParentNumber={pcoParentNumber}
          submissions={submissions}
          onClose={() => setStartNewOpen(false)}
          onDone={async () => {
            await refetchAll();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Count pill — one lifecycle stage of the packages list. The count is  */
/* always printed, zero included: "no sent packages" is an answer.      */
/* ------------------------------------------------------------------ */

function PackagePill({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`pco-pill${active ? ' is-active' : ''}`}
      aria-pressed={active}
      onClick={onClick}
    >
      {label}
      <span className="pco-pill-count">{count}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Per-week active row — fetches its own detail, renders FeaturedCard. */
/* One rendered per in_progress rollup; handles multiple open weeks.   */
/* ------------------------------------------------------------------ */

function ActiveRollupRow({
  pcoId,
  rollupId,
  weekEndingDate,
  reopenedForRevision,
  revisionContext,
  offerClose,
  carryCount,
  pcoParentNumber,
  moveTargets,
  nextNumber,
  onChanged,
  onSent,
}: {
  pcoId: string;
  rollupId: string;
  weekEndingDate: string;
  /** in_progress AND stale — a late PDF reopened this week; it needs resending. */
  reopenedForRevision?: boolean;
  /** What landed since the last send, when that send was, and its total. Absent
   *  on an API older than the field — the banner degrades to generic copy. */
  revisionContext?: RollupRevisionContext;
  /** True when this rollup's submission is the PCO's open default — enables the
   *  "close & start next" offer inside Approve & Send. */
  offerClose: boolean;
  /** Other unsent weeks in this submission — they move to the next one on close. */
  carryCount: number;
  pcoParentNumber: string;
  /** Other OPEN submissions on the PCO — destinations for "Transfer Week". */
  moveTargets: SubmissionSummary[];
  /** Suggested number for "New submission (004.12)" (MAX+1 across the PCO). */
  nextNumber: string;
  onChanged: () => Promise<void>;
  /** Reports a landed send so the tab can flash the week's new sent row —
   *  presentation only, fired after the API resolves, never gates anything. */
  onSent?: (rollupId: string) => void;
}) {
  // Canonical addressing: fetch this exact rollup, not the week's sole-or-
  // default. In a split week two ActiveRollupRows share a (pco, week) — each
  // must render its own submission's figures and send independently.
  const detail = useRollupDetail(pcoId, weekEndingDate, rollupId);

  const refetchAll = async () => {
    await Promise.all([detail.refetch(), onChanged()]);
  };

  // While this week's detail is in flight, hold its slot with a card-shaped
  // skeleton instead of returning null (client, 2026-08-02: weeks "load in
  // chunks" as each fetch lands). Per-slot rather than gating the whole list —
  // the page keeps its structure immediately and no card pops in late.
  if (!detail.data && detail.isLoading) {
    return (
      <div
        aria-hidden
        style={{
          padding: '16px 18px 18px',
          background: 'var(--surface-warm)',
          border: '1px solid var(--rule-strong)',
          borderRadius: 4,
          marginBottom: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <Skeleton style={{ width: 240, height: 16 }} />
        <Skeleton style={{ width: 150, height: 13 }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Skeleton style={{ width: 130, height: 26 }} />
          <Skeleton style={{ width: 130, height: 26 }} />
        </div>
      </div>
    );
  }
  if (!detail.data || detail.data.status !== 'in_progress') return null;

  return (
    <FeaturedRollupCard
      pcoId={pcoId}
      rollupId={rollupId}
      weekEndingDate={weekEndingDate}
      offerClose={offerClose}
      carryCount={carryCount}
      pcoParentNumber={pcoParentNumber}
      moveTargets={moveTargets}
      nextNumber={nextNumber}
      reopenedForRevision={reopenedForRevision}
      revisionContext={revisionContext}
      detail={detail.data}
      onChanged={refetchAll}
      onSent={onSent}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Featured card — one in_progress rollup, readiness + CTAs.          */
/* ------------------------------------------------------------------ */

function FeaturedRollupCard({
  pcoId,
  rollupId,
  weekEndingDate,
  offerClose,
  carryCount,
  pcoParentNumber,
  moveTargets,
  nextNumber,
  reopenedForRevision,
  revisionContext,
  detail,
  onChanged,
  onSent,
}: {
  pcoId: string;
  rollupId: string;
  weekEndingDate: string;
  /** When true (this is the open default submission), the send confirm offers
   *  a checkbox to close this submission and start the next after sending. */
  offerClose: boolean;
  /** Other unsent weeks in this submission that carry to the next on close. */
  carryCount: number;
  pcoParentNumber: string;
  /** Other OPEN submissions on the PCO — destinations for "Transfer Week". */
  moveTargets: SubmissionSummary[];
  /** Suggested number for "New submission (004.12)" (MAX+1 across the PCO). */
  nextNumber: string;
  /** Late PDF reopened this week — it needs resending, and its money is out
   *  of Submitted-to-date until then. */
  reopenedForRevision?: boolean;
  /** What landed since the last send. Rendered by RevisionDetailsTip, whose
   *  copy comes from `lib/revisionBanner.ts` (`revisionDetail`). */
  revisionContext?: RollupRevisionContext;
  detail: RollupDetail;
  onChanged: () => Promise<void>;
  /** Reports a landed send so the tab can flash the week's new sent row. */
  onSent?: (rollupId: string) => void;
}) {
  const navigate = useNavigate();
  const { token, me } = useAuth();
  const project = useSettingsProject();
  const toast = useToast();
  const [sendBusy, setSendBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [closeAfterSend, setCloseAfterSend] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);

  // The close offer shows on any open submission, but only on its LAST unsent
  // week — closing while other weeks are still unsent would strand them (and
  // the server refuses it anyway). carryCount is those other weeks.
  const canOfferClose = offerClose && detail.submission != null && carryCount === 0;
  const currentShort = detail.submission
    ? formatSubmissionShort(detail.pco.number, detail.submission.submission_number)
    : '';

  const daysWithEntries = new Set(detail.contributing_pdfs.map((p) => p.report_date)).size;

  const readinessIssues: string[] = [];
  if (daysWithEntries === 0) readinessIssues.push('No working days entered yet');
  // Undecided parser material candidates are NOT a readiness issue any more
  // (M12, 2026-08-03). The block-save gate that guaranteed a saved block
  // carried none of them is gone, so saved work now routinely does — and with
  // the triage UI retired there is no way to clear one. Left in, it would have
  // disabled Approve & Send on a correct week permanently.
  if (detail.unknown_employee_count > 0) {
    readinessIssues.push(
      `${detail.unknown_employee_count} unknown employee${detail.unknown_employee_count === 1 ? '' : 's'} need resolution`,
    );
  }
  if (detail.unknown_equipment_count > 0) {
    readinessIssues.push(
      `${detail.unknown_equipment_count} unknown equipment row${detail.unknown_equipment_count === 1 ? '' : 's'} need resolution`,
    );
  }
  const ready = readinessIssues.length === 0;

  const doSend = async (payload: SendComposerPayload) => {
    if (!token) return;
    setSendBusy(true);
    setErr(null);
    try {
      // The close rides ON the send request — one round trip, and the server
      // owns the ordering. (It used to be a second api.submissions.close call
      // after the fact, which could interleave with anything.) The send never
      // fails because of the close: refusal comes back as
      // submission_closed:false + submission_close_error.
      const wantClose = canOfferClose && closeAfterSend;
      const result = await api.rollups.submitById(token, rollupId, {
        ...payload,
        ...(wantClose ? { close_submission: true } : {}),
      });
      setConfirmSend(false);
      onSent?.(rollupId);
      const sentBody = `Sent to ${result.recipient_email}. ${result.attached_pdf_count} PDF${result.attached_pdf_count === 1 ? '' : 's'} attached. Invoice total ${formatMoneyCents(result.invoice_total_cents)}.`;

      if (wantClose && result.submission_closed === false) {
        toast.push({ variant: 'success', title: 'Sent', body: sentBody, durationMs: 6000 });
        toast.push({
          variant: 'error',
          title: `Sent, but couldn't close ${currentShort}`,
          body: `${submissionErrorMessageForCode(result.submission_close_error, 'Could not close the submission.', gcName(me))} Once everything here is sent you can close it from the package row.`,
        });
      } else {
        toast.push({
          variant: 'success',
          title: 'Sent',
          body:
            wantClose && result.submission_closed
              ? `${sentBody} Closed ${currentShort}.`
              : sentBody,
          durationMs: 6000,
        });
      }
      await onChanged();
    } catch (e) {
      setErr(rollupActionErrorMessage(e, 'Send failed'));
    } finally {
      setSendBusy(false);
    }
  };

  return (
    // A row of the package's week table (2026-08-05) — same three columns as
    // its sent siblings, so a package reads as one table rather than a card
    // stack. Everything that needs the full width (the revision banner, the
    // readiness summary, a send error) drops onto the row's second grid line.
    <div className="week-row">
      {/* No ACTIVE pill (client, 2026-08-01): an in-progress week wearing an
          Approve & Send button is self-evidently active — the pill restated
          its own surroundings. The package number is gone too: the row above
          this table already says it. */}
      <span className="week-row-day">{formatWeekdayMonthDay(weekEndingDate)}</span>
      <span className="week-row-sub">
        {formatMoneyCents(detail.totals.invoice_total_cents)}
      </span>
      <div className="week-row-actions">
        <button
          type="button"
          className="btn-link"
          style={{ fontSize: 12.5 }}
          onClick={() => navigate(`/pcos/${pcoId}/${weekEndingDate}?rollup=${rollupId}`)}
        >
          View details
        </button>
        {/* Transfer Week stays VISIBLE (client, 2026-08-01: it is filing, not
            a step in the week's own path — and it is a capability, so it does
            not go behind a menu). Quiet link beside View details; the accent
            button is the week's actual next step. */}
        <button
          type="button"
          className="btn-link"
          style={{ fontSize: 12.5 }}
          onClick={() => setMoveOpen(true)}
          title="Transfer this unsent week into another open submission"
        >
          Transfer Week
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-accent btn-compact"
          onClick={() => {
            setCloseAfterSend(false);
            setConfirmSend(true);
          }}
          disabled={sendBusy || !ready}
          title={ready ? '' : `Not ready: ${readinessIssues.join('; ')}`}
        >
          Approve &amp; Send <ArrowRight size={14} aria-hidden />
        </button>
      </div>

      {err && (
        <span className="week-row-extra" style={{ color: 'var(--danger)', fontSize: 12 }}>
          {err}
        </span>
      )}

      {reopenedForRevision && (
        <RevisionBannerChrome density="row" className="week-row-extra">
          {/* HEADLINE ONLY, then one control (Brandon 2026-08-19). The four
              facts this owes the PM — when the GC last saw the week, what he
              was told it cost, which reports landed, what happens next — were
              a paragraph inside a table row and read as a wall. They live in
              the popover now; the row states the fact. Same component and same
              copy as the detail page, so the two can't drift. */}
          <span
            style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}
          >
            <strong style={{ color: 'var(--warning)', fontWeight: 600 }}>
              {REVISION_BANNER_LABEL}
            </strong>
            <RevisionDetailsTip
              context={revisionContext}
              currentInvoiceTotalCents={detail.totals.invoice_total_cents}
              gcLabel={gcName(me)}
            />
          </span>
        </RevisionBannerChrome>
      )}

      {/* Readiness is about THIS week and belongs under it, full width — it
          renders nothing when the week is clean, so a ready row stays one
          line. The subtotal it used to share a row with is the SUBTOTAL
          column now, stated once by the table header. */}
      <div className="week-row-extra">
        <ReadinessSummary
          unknownEmployees={detail.unknown_employee_count}
          unknownEquipment={detail.unknown_equipment_count}
        />
      </div>

      <SendComposerModal
        open={confirmSend}
        pcoId={pcoId}
        weekEndingDate={weekEndingDate}
        rollupId={rollupId}
        pcoNumber={detail.pco.number}
        submissionLabel={currentShort}
        gcLabel={gcName(me)}
        invoiceTotalCents={detail.totals.invoice_total_cents}
        primaryEmail={project.data?.gc_email ?? ''}
        rosterEmails={project.data?.gc_additional_emails ?? []}
        busy={sendBusy}
        error={err}
        footerExtra={
          canOfferClose ? (
            <label
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                fontSize: 13,
                color: 'var(--ink-2)',
                lineHeight: 1.4,
              }}
            >
              <input
                type="checkbox"
                checked={closeAfterSend}
                onChange={(e) => setCloseAfterSend(e.target.checked)}
                disabled={sendBusy}
                style={{ marginTop: 2 }}
              />
              {/* Only rendered on the package's last unsent week (see
                  canOfferClose), so this copy is always true. */}
              <span>
                This was the last week of <strong>{currentShort}</strong> — close it after
                sending.
              </span>
            </label>
          ) : null
        }
        onSend={doSend}
        onCancel={() => setConfirmSend(false)}
      />

      {moveOpen && (
        <MoveWeekModal
          rollupId={rollupId}
          weekEndingDate={weekEndingDate}
          currentLabel={currentShort}
          pcoParentNumber={pcoParentNumber}
          targets={moveTargets}
          nextNumber={nextNumber}
          onCancel={() => setMoveOpen(false)}
          onMoved={async () => {
            setMoveOpen(false);
            await onChanged();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Move-week modal — re-homes one unsent week into another open        */
/* submission on the PCO, or a fresh next one (create_next), which     */
/* also becomes the PCO's collecting default and inherits MOD/FWA.     */
/* The source submission stays OPEN — closing it is a separate act.    */
/* ------------------------------------------------------------------ */

function MoveWeekModal({
  rollupId,
  weekEndingDate,
  currentLabel,
  pcoParentNumber,
  targets,
  nextNumber,
  onCancel,
  onMoved,
}: {
  rollupId: string;
  weekEndingDate: string;
  /** Short label of the submission the week currently sits in ("004.11"). */
  currentLabel: string;
  pcoParentNumber: string;
  /** Other OPEN submissions on the PCO. */
  targets: SubmissionSummary[];
  /** Suggested number for the "New submission" option. */
  nextNumber: string;
  onCancel: () => void;
  onMoved: () => Promise<void>;
}) {
  const { token, me } = useAuth();
  const toast = useToast();
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Mounted only while open, so the lock is unconditional.
  useBodyScrollLock(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const NEW_OPTION = '__new__';

  const destOptions = useMemo(
    () => [
      { value: '', label: 'Select a submission…' },
      ...targets.map((s) => ({
        value: s.id,
        label: `${formatSubmissionLabel(pcoParentNumber, s.submission_number)}${s.is_default_target ? ' (default)' : ''}`,
      })),
      { value: NEW_OPTION, label: `New submission (${formatSubmissionShort(pcoParentNumber, nextNumber)})` },
    ],
    [targets, pcoParentNumber, nextNumber],
  );

  const confirm = async () => {
    if (!token || !choice) return;
    setBusy(true);
    setErr(null);
    try {
      const result = await api.rollups.moveSubmission(
        token,
        rollupId,
        choice === NEW_OPTION ? { create_next: true } : { target_submission_id: choice },
      );
      toast.push({
        variant: 'success',
        title: 'Transferred',
        body: `Week ${formatWeekRange(weekEndingDate)} transferred to ${formatSubmissionShort(pcoParentNumber, result.target_submission_number)}.`,
        durationMs: 6000,
      });
      await onMoved();
    } catch (e) {
      setErr(submissionErrorMessage(e, 'Could not transfer the week.', gcName(me)));
      setBusy(false);
    }
  };

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Transfer week"
    >
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="confirm-modal-head">
          <div className="confirm-modal-title">Transfer week</div>
        </div>
        <div className="confirm-modal-body">
          <div
            style={{
              background: 'var(--surface-warm, var(--surface))',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              padding: '8px 10px',
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            Week {formatWeekRange(weekEndingDate)} · currently in{' '}
            <span className="mono">{currentLabel}</span>
          </div>

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Destination submission
          </label>
          <Dropdown
            options={destOptions}
            value={choice}
            onChange={setChoice}
            disabled={busy}
            style={{ width: '100%' }}
          />

          {/* "New submission" does more than mint a number, and the two
              consequences it doesn't spell out are the ones that surprise
              people: it takes over as the collecting default, and the package
              you moved the week OUT of does not close by itself. */}
          {choice === NEW_OPTION && (
            <p className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
              Opens {formatSubmissionShort(pcoParentNumber, nextNumber)} as this
              PCO&rsquo;s new collecting submission, inheriting the MOD and FWA.{' '}
              <span className="mono">{currentLabel}</span> stays open until you close
              it — use &ldquo;Close submission&rdquo; on its row once its weeks are
              sent.
            </p>
          )}

          {err && (
            <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>{err}</div>
          )}
        </div>
        <div className="confirm-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-cta"
            onClick={confirm}
            disabled={busy || !choice}
          >
            {busy ? 'Transferring…' : 'Transfer'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Warnings only, then the money (client, 2026-08-01). The card used to carry
 * three always-on counter lines — "N working days entered", "0 materials
 * pending review", "0 unknown employees · 0 unknown equipment" — which on a
 * healthy week were three lines of zeros above the number the PM came for.
 * A count of zero is not information; only a nonzero one is, and it renders
 * amber because it BLOCKS the send. Working days entered is gone at any
 * value: the empty-week case still stops the send and still says so in the
 * disabled button's hint.
 *
 * The undecided-material warning was the third one and is gone entirely (M12,
 * 2026-08-03) — see the readinessIssues note above. Every warning here must be
 * one the PM can actually clear.
 */
function ReadinessSummary({
  unknownEmployees,
  unknownEquipment,
}: {
  unknownEmployees: number;
  unknownEquipment: number;
}) {
  const warnings: string[] = [];
  if (unknownEmployees > 0) {
    warnings.push(
      `${unknownEmployees} unknown employee${unknownEmployees === 1 ? '' : 's'}`,
    );
  }
  if (unknownEquipment > 0) {
    warnings.push(
      `${unknownEquipment} unknown equipment row${unknownEquipment === 1 ? '' : 's'}`,
    );
  }

  // Subtotal moved to the action row (client, 2026-08-01) — with no warnings
  // this renders nothing at all, and the card gives that height back.
  if (warnings.length === 0) return null;
  return (
    <div
      style={{ display: 'flex', flexWrap: 'wrap', columnGap: 14, rowGap: 2, lineHeight: 1.7 }}
    >
      {warnings.map((w) => (
        <span key={w} style={{ color: 'var(--warning)', fontSize: 13 }}>
          {w}
        </span>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Closed/other row — Pending + Approved (+ rare non-featured active).*/
/* View, plus Reopen (closed).                                        */
/*                                                                    */
/* NO week-level "Mark approved" (removed 2026-07-29). There is ONE   */
/* approval concept at the T&M level: the SUBMISSION is the unit, and */
/* approving it cascades to its weeks (submissions/lifecycle.ts). Two */
/* buttons for one real-world event let a week sit orange inside a    */
/* green package. The chip below turns green via that cascade.        */
/* ------------------------------------------------------------------ */

function ClosedRollupRow({
  pcoId,
  rollup,
  flash = false,
  onChanged,
}: {
  pcoId: string;
  rollup: RollupListItem;
  /** True right after this week was sent from this tab — the row flashes and
   *  scrolls into view so the eye lands where the week went. */
  flash?: boolean;
  onChanged: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!flash) return;
    rowRef.current?.scrollIntoView({
      block: 'nearest',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }, [flash]);
  const { token } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // Reopen is the only confirm left on a week row — approval moved to the
  // submission (2026-07-29).
  const [confirmKind, setConfirmKind] = useState<'reopen' | null>(null);
  const [editTotalOpen, setEditTotalOpen] = useState(false);
  const isClosed = rollup.status === 'submitted' || rollup.status === 'approved' || rollup.status === 'paid';
  const sentDay = sentOnLabel(rollup.submitted_at);

  const run = async (fn: () => Promise<unknown>, successTitle: string, successBody: string) => {
    if (!token) return;
    setBusy(true);
    try {
      await fn();
      setConfirmKind(null);
      toast.push({ variant: 'success', title: successTitle, body: successBody, durationMs: 6000 });
      await onChanged();
    } catch (e) {
      toast.push({
        variant: 'error',
        title: 'Failed',
        // The server's own sentence when it sent one. `ApiCallError.message`
        // is the machine CODE, which is how this toast used to read
        // "Failed / imported_rollup".
        body: rollupActionErrorMessage(e, 'Something went wrong'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    // A row of the package's week table (2026-08-05). The package number is
    // gone from every week row — the row it hangs under already says it, and
    // repeating it on each week was the loudest thing in the stack.
    <div ref={rowRef} className={`week-row${flash ? ' sent-flash' : ''}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span className="week-row-day">
          {formatWeekdayMonthDay(rollup.week_ending_date)}
        </span>
        {/* Status renders only when it says something the row doesn't
            (client, 2026-08-01): a Sent week — with its send date — as quiet
            text, which matters most when the week sits inside a still-Open
            package; approved/paid weeks show nothing (the package already
            says it); anything odd falls back to the pill. */}
        {rollup.status === 'submitted' ? (
          <span className="muted" style={{ fontSize: 12 }}>
            Sent{sentDay ? ` ${formatDateMd(sentDay)}` : ''}
          </span>
        ) : rollup.status === 'approved' || rollup.status === 'paid' ? null : (
          <StatusPill kind={pillKindFor(rollup.status)}>{statusLabel(rollup.status)}</StatusPill>
        )}
        {rollup.imported && (
          <span
            title="Loaded from the original weekly file (historical import)"
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
              border: '1px solid var(--rule)',
              padding: '1px 6px',
              borderRadius: 3,
            }}
          >
            Imported
          </span>
        )}
      </div>
      <span className="week-row-sub">
        {formatMoneyCents(rollup.totals.invoice_total_cents)}
      </span>
      <div className="week-row-actions">
        <button
          type="button"
          className="btn-link"
          style={{ fontSize: 12.5 }}
          onClick={() =>
            navigate(`/pcos/${pcoId}/${rollup.week_ending_date}?rollup=${rollup.id}`)
          }
        >
          View details
        </button>
        {/* An imported week can never be reopened — the server 409s, because
            there are no lines to rebuild from (register I1). Offering the
            control anyway meant a button whose only possible outcome was a
            failure toast. What it CAN accept is a correction to the one
            hand-typed figure it carries, so that is what it offers instead.
            Non-imported weeks keep Reopen untouched. */}
        {isClosed && !rollup.imported && (
          <button
            type="button"
            className="btn-link"
            style={{ fontSize: 12.5 }}
            disabled={busy}
            onClick={() => setConfirmKind('reopen')}
          >
            Reopen
          </button>
        )}
        {rollup.imported && (
          <button
            type="button"
            className="btn-link"
            style={{ fontSize: 12.5 }}
            disabled={busy}
            onClick={() => setEditTotalOpen(true)}
            title="Correct the hand-entered total on this imported week"
          >
            Edit total
          </button>
        )}
      </div>

      {editTotalOpen && (
        <EditImportedTotalModal
          rollupId={rollup.id}
          weekEndingDate={rollup.week_ending_date}
          currentTotalCents={rollup.totals.invoice_total_cents}
          onCancel={() => setEditTotalOpen(false)}
          onSaved={async () => {
            setEditTotalOpen(false);
            await onChanged();
          }}
        />
      )}

      <ConfirmModal
        open={confirmKind === 'reopen'}
        title="Reopen this rollup?"
        body="Flips it back to Active so you can edit and re-send. The prior submission stays on record; the next send is marked REVISED."
        confirmLabel="Reopen"
        busyLabel="Reopening…"
        busy={busy}
        onConfirm={() =>
          run(
            () => api.rollups.reopenById(token!, rollup.id),
            'Reopened',
            `${formatSubmissionLabel(rollup.pco.pco_number, rollup.submission_number)} moved back to Active.`,
          )
        }
        onCancel={() => setConfirmKind(null)}
      />
    </div>
  );
}

const pillKindFor = rollupPillKind;

/**
 * "Active" is this tab's word for a week still collecting — it's what the
 * filter chips, the reopen confirmation and the help copy all say here. Every
 * other status comes from the shared map, so the sent/approved/paid vocabulary
 * can't drift between surfaces again.
 */
function statusLabel(status: RollupStatus): string {
  return status === 'in_progress' ? 'Active' : rollupStatusLabel(status);
}
